import type { IDropDownItem } from "@similarweb/ui-components";
import { Dropdown } from "@similarweb/ui-components";
import { StyledDropdownParent } from "app/pages/sales-intelligence/common-components/dropdown/styles";
import {
    StyledTruncatedInnerText
} from "app/pages/sales-intelligence/common-components/dropdown/DropdownLabelWithCount/styles";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import SignalTemplateDropdownItem from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalTemplatesDropdown/SignalTemplateDropdownItem";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import type { ComponentType, ReactElement } from "react";
import { useMemo } from "react";

import type SignalFilters from "../../../models/core/SignalFilters";
import type SignalTemplate from "../../../models/templates/SignalTemplate";
import FilterButtonLabel from "../../filters/shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { DROPDOWN_ID, DROPDOWN_WIDTH, UNKNOWN_SIGNAL_TRANSLATABLE } from "./const";
import { StyledDropdownItemsDivider } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalTemplatesDropdown/styles";
import withSignalLifeCycleTag from "app/pages/sales-intelligence/sub-modules/signals/hoc/withSignalLifeCycleTag";

export type SignalTemplatesDropdownProps = {
    dropdownPopupWidth?: number;
    dropdownPopupHeight?: number;
    defaultOpen?: boolean;
    selectedTemplate: Readonly<SignalTemplate<SignalFilters>> | null;
    groupedTemplates: readonly SignalTemplate<SignalFilters>[][];
    renderItem?: ComponentType<{
        template: Readonly<SignalTemplate<SignalFilters>>;
    }>;
    hideTemplateDescriptionTooltip?: boolean;
    onSelect(option: Readonly<SignalTemplate<SignalFilters>>): void;
};

const SignalTemplatesDropdown = (props: SignalTemplatesDropdownProps) => {
    const {
        dropdownPopupWidth = DROPDOWN_WIDTH,
        dropdownPopupHeight,
        defaultOpen = false,
        selectedTemplate,
        groupedTemplates,
        hideTemplateDescriptionTooltip = false,
        renderItem: renderItemFromProps,
        onSelect,
    } = props;

    const defaultTemplateItem = hideTemplateDescriptionTooltip
        ? withSignalLifeCycleTag((itemProps) => (
              <SignalTemplateDropdownItem
                  {...itemProps}
                  hideDescriptionTooltip
              />
          ))
        : withSignalLifeCycleTag(SignalTemplateDropdownItem);

    const TemplateDropdownItem = renderItemFromProps ?? defaultTemplateItem;

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, defaultOpen);

    const dropdownButtonText = selectedTemplate?.name ?? UNKNOWN_SIGNAL_TRANSLATABLE;

    const selectedId: Record<string, boolean> | null = useMemo(() => {
        if (selectedTemplate === null) {return {};}

        return { [selectedTemplate.alias]: true };
    }, [selectedTemplate]);

    const handleSelect = ({ id }: IDropDownItem) => {
        const templates = groupedTemplates.flat();
        const selectedTemplate = templates.find(({ alias }) => alias === id);

        if (selectedTemplate === undefined) {
            console.error(`Signal templates dropdown: unable to select option ${id}`);

            return;
        }

        onSelect(selectedTemplate);
    };

    const renderOptions = (templates: readonly SignalTemplate<SignalFilters>[]): ReactElement[] =>
        templates.map((template) => (
            <div
                key={template.alias}
                id={template.alias}
            >
                <TemplateDropdownItem template={template} />
            </div>
        ));

    const renderGroupedOptions = (): ReactElement[] =>
        groupedTemplates.flatMap((templates, index) => {
            const items = renderOptions(templates);

            // if the last group
            if (index === groupedTemplates.length - 1) {
                return items;
            }

            return [...items, <StyledDropdownItemsDivider />];
        });

    return (
        <StyledDropdownParent id={DROPDOWN_ID}>
            <Dropdown
                ref={dropdownRef}
                appendTo={`#${DROPDOWN_ID}`}
                selectedIds={selectedId}
                width={dropdownPopupWidth}
                onClick={handleSelect}
                dropdownPopupHeight={dropdownPopupHeight}
            >
                <SignalFilterDropdownButton
                    key="dd-button"
                    id={`${DROPDOWN_ID}-button`}
                    placeholder={{ key: "si.signals.filters.common-text.select" }}
                    hasValue={selectedTemplate !== null}
                >
                    <FilterButtonLabel>
                        <I18n
                            translatable={dropdownButtonText}
                            element={StyledTruncatedInnerText}
                        />
                    </FilterButtonLabel>
                </SignalFilterDropdownButton>

                {...renderGroupedOptions()}
            </Dropdown>
        </StyledDropdownParent>
    );
};

export default SignalTemplatesDropdown;
